import type { FormSettings } from ".";

interface Bucket {
	count: number;
	resetAt: number;
}

export interface WindowLimit {
	max: number;
	windowMs: number;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const SWEEP_EVERY_MS = MINUTE;

/** Used when a form sets no `rateLimit` of its own. */
export const DEFAULT_SUBMIT_LIMITS = { perMinute: 5, perDayPerIp: 50 };
/** Client confirmations to one address, so a bot can't flood a stranger's inbox. */
export const CONFIRMATIONS_PER_ADDRESS: WindowLimit = {
	max: 3,
	windowMs: HOUR,
};

/**
 * Fixed-window counters held in this process. Each region runs one process, so this is
 * per region and resets on deploy — enough to stop floods without a shared store.
 */
export function createRateLimiter(): (
	key: string,
	limit: WindowLimit,
	now?: number
) => boolean {
	const buckets = new Map<string, Bucket>();
	let lastSweep = 0;
	return (key, limit, now = Date.now()) => {
		if (now - lastSweep > SWEEP_EVERY_MS) {
			for (const [k, b] of buckets) {
				if (now >= b.resetAt) {
					buckets.delete(k);
				}
			}
			lastSweep = now;
		}
		const bucket = buckets.get(key);
		if (!bucket || now >= bucket.resetAt) {
			buckets.set(key, { count: 1, resetAt: now + limit.windowMs });
			return true;
		}
		if (bucket.count >= limit.max) {
			return false;
		}
		bucket.count += 1;
		return true;
	};
}

const allowSubmit = createRateLimiter();
const allowConfirmationTo = createRateLimiter();

/** Per visitor per form: the form's own limits, else the defaults. `false` = refuse (429). */
export function allowFormSubmit(
	formId: string,
	ip: string | null,
	settings: FormSettings | null | undefined
): boolean {
	const key = `${formId}:${ip ?? "unknown"}`;
	const perMinute =
		settings?.rateLimit?.perMinute ?? DEFAULT_SUBMIT_LIMITS.perMinute;
	const perDay =
		settings?.rateLimit?.perDayPerIp ?? DEFAULT_SUBMIT_LIMITS.perDayPerIp;
	return (
		allowSubmit(`m:${key}`, { max: perMinute, windowMs: MINUTE }) &&
		allowSubmit(`d:${key}`, { max: perDay, windowMs: DAY })
	);
}

/** `false` once this address has had its share of client confirmations this hour. */
export function allowConfirmationEmail(address: string): boolean {
	return allowConfirmationTo(
		address.trim().toLowerCase(),
		CONFIRMATIONS_PER_ADDRESS
	);
}

export const TOO_MANY_SUBMISSIONS =
	"Too many submissions — please wait a moment and try again.";

// IPv4 or IPv6 characters only — the value is stored in an `inet` column, so junk would fail the insert.
const IP_LIKE = /^(?:\d{1,3}\.){3}\d{1,3}$|^(?=.*:)[0-9a-fA-F:.]{2,45}$/;

/**
 * The visitor's IP. nginx sets `X-Real-IP` and appends to `X-Forwarded-For`, so the first
 * forwarded value is whatever the client sent — use the last one only as a fallback.
 */
export function requestClientIp(headers: Headers): string | null {
	const real = headers.get("x-real-ip")?.trim();
	const last = headers.get("x-forwarded-for")?.split(",").at(-1)?.trim();
	const ip = real || last;
	return ip && IP_LIKE.test(ip) ? ip : null;
}
